import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { message, Select } from "antd";
import { getFuelEfficiency } from "../../../API/fuelEfficiency";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import LineChart from "../../../Components/Charts/LineChart";
import BarChart from "../../../Components/Charts/BarChart";
import GenericTable from "../../../Components/GenericTable";

const { Option } = Select;

const defaultFuelEffiData = {
  total_fuel_consumed: 0,
  start_volume: 0,
  end_volume: 0,
  KmL: 0,
  LHr: 0,
  fuel_usage: {},
  fuel_usage_km: {},
  fuel_L_vs_KM: {},
  fuel_economy: {},
  fuel_trend: {},
  fleet_size: 0,
  fleet_avg: "",
  average: "",
  correlation: {},
  idlevsmovement: {},
  fuel_usage_idlevsmovement: {},
  worst_idlevsmovement: {},
  worst_fuel_usage_idlevsmovement: {},
  all_vehicles: [],
};

const FuelEfficiency = () => {
  const [fuelEffiData, setFuelEffiData] = useState(defaultFuelEffiData);
  const [economyKeys, setEconomyKeys] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [loading, setLoading] = useState(true);
  const { from, to } = useDateFilter();

  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchfuelEffiData = async () => {
      try {
        setLoading(true);
        const response = await getFuelEfficiency(to, from);
        setFuelEffiData(response.data);
        const economyKeys = Object.keys(response.data?.fuel_efficiency || {});
        await setEconomyKeys(economyKeys);
        // default to first key if exists
        if (economyKeys.length > 0) {
          setSelectedKey(economyKeys[0]);
        }
        setLoading(false);
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
      }
      setLoading(false);
    };

    fetchfuelEffiData();
  }, [to, from]);

  const AllVehicleColumns = [
    {
      title: <span className="vehicle-title">Vehicle Name</span>,
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      sorter: (a, b) =>
        a.vehicle_name.localeCompare(b.vehicle_name, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Mileage",
      dataIndex: "mileage",
      key: "mileage",
      sorter: (a, b) =>
        a.mileage.localeCompare(b.mileage, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Current Fuel (L)",
      dataIndex: "current_fuel",
      key: "current_fuel",
      sorter: (a, b) => a.current_fuel - b.current_fuel,
    },
    {
      title: "Engine On Hours",
      dataIndex: "engine_on_hours",
      key: "engine_on_hours",
      sorter: (a, b) =>
        a.engine_on_hours.localeCompare(b.engine_on_hours, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Idle Hours",
      dataIndex: "idle_hours",
      key: "idle_hours",
      sorter: (a, b) =>
        a.idle_hours.localeCompare(b.idle_hours, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Movement Hours",
      dataIndex: "movement_hours",
      key: "movement_hours",
      sorter: (a, b) =>
        a.movement_hours.localeCompare(b.movement_hours, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Fuel Wasted",
      dataIndex: "fuel_wasted",
      key: "fuel_wasted",
      sorter: (a, b) => a.fuel_wasted - b.fuel_wasted,
    },
    {
      title: "Idle Ratio %",
      dataIndex: "idle_ratio",
      key: "idle_ratio",
      sorter: (a, b) => a.idle_ratio - b.idle_ratio,
    },
    {
      title: "Idle Fuel Cost",
      dataIndex: "idle_fuel_cost",
      key: "idle_fuel_cost",
      render: (value) => `$${value}`,
      sorter: (a, b) => a.idle_fuel_cost - b.idle_fuel_cost,
    },
  ];

  const chartKey = useMemo(() => {
    return JSON.stringify(selectedKey);
  }, [selectedKey]);

  return (
    <div className="fuel-efficiency">
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <div className="rows-container">
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Start Volume</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {fuelEffiData?.start_volume} L
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Total Fuel Consumed</span>
                <span className="value-box" style={{ color: "#DFB400" }}>
                  {fuelEffiData?.total_fuel_consumed} L
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fuel Km/L and L/Hr</span>
                <span className="value-box" style={{ color: "#155DFC" }}>
                  {fuelEffiData?.KmL}Km/L - {fuelEffiData?.LHr}L/Hr
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">End Volume</span>
                <span className="value-box" style={{ color: "#FB3748" }}>
                  {fuelEffiData?.end_volume} L
                </span>
              </div>
            </div>
          </div>
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Fuel Economy</span>
                <span className="value-box" style={{ color: "#DFB400" }}>
                  {fuelEffiData?.fleet_avg}
                </span>
              </div>
            </div>

            {/* <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Fuel Economy Trend</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {fuelEffiData?.average}
                </span>
              </div>
            </div> */}
          </div>

          <div className="row">
            <div className="graph">
              <BarChart
                graphData={{
                  labels: fuelEffiData?.fuel_usage?.labels,
                  usage: fuelEffiData?.fuel_usage?.usage,
                }}
                lengthKeys={1}
                colourTheme={["#2662D9"]}
                graphTitle="Fuel Usage"
                units={["L"]}
                selectTenByDefault={false}
                classNameCSS="fuel-usage-bar"
              />
            </div>
            <div className="graph">
              <BarChart
                graphData={fuelEffiData?.fuel_usage_km || {}}
                lengthKeys={1}
                colourTheme={["#2662D9"]}
                graphTitle="Fuel Usage per 100 km"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="fuel-usage-per-km"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={{
                  labels: fuelEffiData?.fuel_L_vs_KM.labels,
                  LKm: fuelEffiData?.fuel_L_vs_KM.LKM,
                  LH: fuelEffiData?.fuel_L_vs_KM.LH,
                }}
                lengthKeys={2}
                colourTheme={["#2662D9", "#FB3748"]}
                graphTitle="Fuel L/H Vs L/KM"
                selectTenByDefault={false}
                classNameCSS="fuel-lh"
              />
            </div>
            <div className="graph">
              <BarChart
                graphData={fuelEffiData?.fuel_economy || {}}
                lengthKeys={1}
                colourTheme={["#1FC16B"]}
                graphTitle="Fuel Economy"
                selectTenByDefault={false}
                units={["Km/L"]}
                classNameCSS="fuel-economy"
              />
            </div>
          </div>
          <div className="row">
            <div className="graph">
              {economyKeys.length > 0 && (
                <Select
                  value={selectedKey}
                  style={{ width: "100%", marginBottom: "1rem" }}
                  onChange={(value) => setSelectedKey(value)}
                >
                  {economyKeys.map((key) => (
                    <Option key={key} value={key}>
                      {key}
                    </Option>
                  ))}
                </Select>
              )}
              <LineChart
                key={chartKey}
                graphData={fuelEffiData?.fuel_efficiency?.[selectedKey] || {}}
                lengthKeys={1}
                colourTheme={["#1FC16B"]}
                graphTitle="Fuel Economy Trend"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="economy-trend"
              />
            </div>

            <div className="graph">
              <LineChart
                graphData={fuelEffiData?.correlation || {}}
                lengthKeys={3}
                colourTheme={["#1FC16B", "#FB3748", "#DFB400"]}
                graphTitle="Correlation Between Vehicle Usage Variables"
                selectTenByDefault={false}
                units={["km", "hr", "hr"]}
                classNameCSS="correlation"
              />
            </div>
          </div>

          <div className="row-heading">Top Performing Vehicles</div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={fuelEffiData?.idlevsmovement || {}}
                lengthKeys={2}
                colourTheme={["#FB3748", "#2662D9"]}
                graphTitle="Idle Vs Movement Hours"
                selectTenByDefault={false}
                units={["%", "%"]}
                classNameCSS="idle-vs-mov"
              />
            </div>

            <div className="graph">
              <LineChart
                graphData={fuelEffiData?.fuel_usage_idlevsmovement || {}}
                lengthKeys={2}
                colourTheme={["#FB3748", "#2662D9"]}
                graphTitle="Fuel Usage (Idle vs Movement)"
                selectTenByDefault={false}
                units={["L", "L"]}
                classNameCSS="fuel-usage"
              />
            </div>
          </div>

          <div className="row-heading">Worst Performing Vehicles</div>
          <div className="row">
            <div className="graph">
              <LineChart
                graphData={fuelEffiData?.worst_idlevsmovement || {}}
                lengthKeys={2}
                colourTheme={["#FB3748", "#2662D9"]}
                graphTitle="Idle Vs Movement Hours"
                selectTenByDefault={false}
                units={["%", "%"]}
                classNameCSS="idle-vs-mov"
              />
            </div>

            <div className="graph">
              <LineChart
                graphData={fuelEffiData?.worst_fuel_usage_idlevsmovement || {}}
                lengthKeys={2}
                colourTheme={["#FB3748", "#2662D9"]}
                graphTitle="Fuel Usage (Idle vs Movement)"
                selectTenByDefault={false}
                units={["L", "L"]}
                classNameCSS="fuel-usage"
              />
            </div>
          </div>
          <div className="row">
            <div className="graph table">
              <GenericTable
                title="All Vehicles"
                dataSource={fuelEffiData?.all_vehicles}
                columns={AllVehicleColumns}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelEfficiency;
